import React from 'react';
import { AlertCircle, Users, MessageSquare, CheckCircle } from 'lucide-react';
import type { MessagingRestriction } from '../../store/messageStore';

interface MessageRestrictionWarningProps {
  restriction: MessagingRestriction;
  className?: string;
}

const MessageRestrictionWarning: React.FC<MessageRestrictionWarningProps> = ({
  restriction,
  className = ''
}) => {
  const getWarningConfig = () => {
    switch (restriction.reason) {
      case 'GROUPS_REQUIRED':
        return {
          icon: Users,
          title: 'Geen gedeelde groep',
          message: restriction.message || 'Je kunt alleen berichten sturen naar gebruikers die je via groepen hebt ontmoet',
          bgColor: 'bg-neon-cyan/[.07]',
          borderColor: 'border-neon-cyan/25',
          textColor: 'text-fg-body',
          iconColor: 'text-neon-cyan',
          suggestion: 'Sluit je aan bij een groep waar deze gebruiker ook lid van is om contact op te nemen.'
        };

      case 'SAME_USER':
        return {
          icon: AlertCircle,
          title: 'Ongeldig',
          message: restriction.message || 'Je kunt geen berichten naar jezelf sturen',
          bgColor: 'bg-neon-pink/[.08]',
          borderColor: 'border-neon-pink/30',
          textColor: 'text-fg-body',
          iconColor: 'text-neon-pink-soft',
          suggestion: null
        };

      case 'PREVIOUS_CONVERSATION':
        return {
          icon: CheckCircle,
          title: 'Gesprek toegestaan',
          message: restriction.message || 'Je kunt berichten sturen omdat jullie eerder hebben gechat',
          bgColor: 'bg-neon-lime/[.08]',
          borderColor: 'border-neon-lime/25',
          textColor: 'text-fg-body',
          iconColor: 'text-neon-lime',
          suggestion: 'Ook al zitten jullie niet meer in dezelfde groep, kun je het gesprek voortzetten.'
        };

      case 'SHARED_GROUPS':
        return {
          icon: CheckCircle,
          title: 'Gesprek toegestaan',
          message: restriction.message || 'Je kunt berichten sturen omdat jullie in dezelfde groep zitten',
          bgColor: 'bg-neon-lime/[.08]',
          borderColor: 'border-neon-lime/25',
          textColor: 'text-fg-body',
          iconColor: 'text-neon-lime',
          suggestion: null
        };

      default:
        return {
          icon: MessageSquare,
          title: 'Onbekende status',
          message: restriction.message || 'Kon berichtstatus niet bepalen',
          bgColor: 'bg-neon-cyan/[.07]',
          borderColor: 'border-neon-cyan/25',
          textColor: 'text-fg-body',
          iconColor: 'text-neon-cyan',
          suggestion: null
        };
    }
  };

  const config = getWarningConfig();
  const Icon = config.icon;

  // Only show cards when users CANNOT message each other
  if (restriction.canMessage) {
    return null;
  }

  return (
    <div className={`rounded-field border p-4 ${config.bgColor} ${config.borderColor} ${className}`}>
      <div className="flex items-start space-x-3">
        <Icon className={`h-5 w-5 mt-0.5 ${config.iconColor} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-fg-bright">
            {config.title}
          </p>
          <p className={`text-sm mt-1 ${config.textColor}`}>
            {config.message}
          </p>
          {config.suggestion && (
            <p className="text-xs mt-2 text-fg-muted">
              💡 {config.suggestion}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageRestrictionWarning;